import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import { Activity, Calendar, Loader2, ArrowRight, Clock, ArrowRightLeft } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';

// Turnover gauge ring (SVG)
function TurnoverGauge({ rate, isDark }) {
  const r = 32, stroke = 6, circ = 2 * Math.PI * r;
  const clampedRate = Math.min(rate, 50);
  const pct = clampedRate / 50; // 50% = full ring
  const color = rate > 25 ? '#ef4444' : rate > 15 ? '#f97316' : '#22c55e';
  return (
    <div className="flex flex-col items-center">
      <svg width={80} height={80} className="-rotate-90">
        <circle cx={40} cy={40} r={r} fill="none" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth={stroke} />
        <circle cx={40} cy={40} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <div className="absolute mt-5 flex flex-col items-center">
        <span className="text-lg font-bold" style={{ color }}>{rate}%</span>
        <span className={`text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>annualized</span>
      </div>
    </div>
  );
}

// Pipeline flow
function PipelineFlow({ metrics, isDark }) {
  const events = metrics?.upcoming_events || [];
  const recent = metrics?.recent_changes || [];

  const noticeCount = events.filter(e => e.type === 'vacancy').length;
  const vacantCount = events.filter(e => e.type === 'lease_expiry').length;
  const leasedCount = events.filter(e => e.type === 'move_in').length;

  // From recent changes, count transitions
  const recentToOccupied = recent.filter(c => c.to === 'occupied').length;

  const steps = [
    { label: 'Notice', count: noticeCount, text: 'text-orange-500', border: 'border-orange-500' },
    { label: 'Vacant', count: vacantCount, text: 'text-red-500', border: 'border-red-500' },
    { label: 'Leased', count: leasedCount, text: 'text-blue-500', border: 'border-blue-500' },
    { label: 'Occupied', count: recentToOccupied, text: 'text-green-500', border: 'border-green-500' },
  ];

  return (
    <div className="flex items-center justify-center gap-1">
      {steps.map((step, i) => (
        <React.Fragment key={step.label}>
          <div className="flex flex-col items-center gap-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 ${step.text} ${step.border}`}>
              {step.count}
            </div>
            <span className={`text-[10px] font-medium ${step.text}`}>{step.label}</span>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight size={14} className={`mt-[-12px] ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function AitPropertyAnalytics({ propertyId }) {
  const { theme } = useTheme();
  const { fetchPropertyAnalytics } = useAit();
  const isDark = theme === 'dark';

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState(12);
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    fetchPropertyAnalytics(propertyId, months).then(data => {
      setMetrics(data);
      setLoading(false);
    });
  }, [propertyId, months, fetchPropertyAnalytics]);

  const filteredEvents = useMemo(() => {
    if (!metrics?.upcoming_events) return [];
    if (eventFilter === 'all') return metrics.upcoming_events;
    return metrics.upcoming_events.filter(e => e.type === eventFilter);
  }, [metrics?.upcoming_events, eventFilter]);

  // Countdown strip counts
  const eventCounts = useMemo(() => {
    if (!metrics?.upcoming_events) return {};
    const counts = { move_in: 0, vacancy: 0, lease_expiry: 0, recert: 0 };
    metrics.upcoming_events.forEach(e => { if (counts[e.type] !== undefined) counts[e.type]++; });
    return counts;
  }, [metrics?.upcoming_events]);

  const tableBorder = isDark ? 'border-gray-700' : 'border-gray-200';
  const tableHead = isDark ? 'bg-gray-750' : 'bg-gray-50';
  const thCls = `text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-blue-500" size={24} />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className={`text-center py-16 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        No analytics data available.
      </div>
    );
  }

  const bandData = Object.entries(metrics.band_turnover || {}).map(([band, count]) => ({
    band: `${band}%`, count, fill: Number(band) >= 70 ? '#ef4444' : Number(band) >= 50 ? '#3b82f6' : '#22c55e'
  }));

  const volumeData = Object.entries(metrics.monthly_volume || {}).map(([m, c]) => ({ month: m, count: c }));

  const tooltipStyle = {
    backgroundColor: isDark ? '#1f2937' : '#fff',
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    borderRadius: 8, fontSize: 11
  };

  return (
    <div className="space-y-5">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium flex items-center gap-2 text-gray-900 dark:text-white">
          <Activity size={14} className="text-blue-500" />
          Property Analytics
        </h2>
        <div className="flex gap-1">
          {[3, 6, 12].map(m => (
            <button
              key={m}
              onClick={() => setMonths(m)}
              className={`px-2.5 py-1 rounded text-xs ${
                months === m
                  ? 'bg-blue-600 text-white'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m}mo
            </button>
          ))}
        </div>
      </div>

      {/* Top row: Turnover gauge + Key metrics + Pipeline */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Turnover gauge */}
        <div className={`rounded-xl border p-4 flex flex-col items-center justify-center ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Turnover Rate</p>
          <div className="relative">
            <TurnoverGauge rate={metrics.turnover_rate} isDark={isDark} />
          </div>
          <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {metrics.turnover_count} vacancy event{metrics.turnover_count !== 1 ? 's' : ''} in {metrics.period_months}mo
          </p>
        </div>

        {/* Key metrics */}
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <p className={`text-[10px] uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Key Metrics</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Avg Vacancy', value: metrics.avg_vacancy_days != null ? `${metrics.avg_vacancy_days}d` : '—', sub: `${metrics.vacancy_samples} fills`, color: metrics.avg_vacancy_days > 30 ? 'text-red-500' : metrics.avg_vacancy_days > 14 ? 'text-orange-500' : 'text-green-500' },
              { label: 'Lease-Up Speed', value: metrics.avg_lease_up_days != null ? `${metrics.avg_lease_up_days}d` : '—', sub: `${metrics.lease_up_samples} move-ins`, color: 'text-blue-500' },
              { label: 'Notice Period', value: metrics.avg_notice_days != null ? `${metrics.avg_notice_days}d` : '—', sub: `${metrics.notice_samples} notices`, color: 'text-blue-500' },
              { label: 'Total Changes', value: metrics.total_status_changes, sub: `last ${metrics.period_months}mo` },
            ].map(s => (
              <div key={s.label}>
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
                <p className={`text-lg font-semibold leading-tight ${s.color || ''}`}>{s.value}</p>
                <p className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{s.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Vacancy pipeline */}
        <div className={`rounded-xl border p-4 flex flex-col items-center justify-center ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <p className={`text-[10px] uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Vacancy Pipeline</p>
          <PipelineFlow metrics={metrics} isDark={isDark} />
          <p className={`text-[10px] mt-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Current transition flow</p>
        </div>
      </div>

      {/* Charts row: Band turnover bar + Activity trend */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Band turnover horizontal bar chart */}
        {bandData.length > 0 && (
          <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className="text-xs font-medium mb-3 text-gray-900 dark:text-white">Turnover by Designation</h3>
            <ResponsiveContainer width="100%" height={Math.max(100, bandData.length * 32 + 20)}>
              <BarChart data={bandData} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }} />
                <YAxis type="category" dataKey="band" width={50} tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={14}>
                  {bandData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Activity line chart */}
        {volumeData.length > 1 && (
          <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className="text-xs font-medium mb-3 text-gray-900 dark:text-white">Status Change Activity</h3>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={volumeData} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="actFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 9, fill: isDark ? '#6b7280' : '#9ca3af' }} />
                <YAxis tick={{ fontSize: 9, fill: isDark ? '#6b7280' : '#9ca3af' }} width={24} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="url(#actFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Building turnover table (only if >1 building) */}
      {metrics.per_building_turnover?.length > 1 && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <h3 className="text-xs font-medium mb-3 text-gray-900 dark:text-white">Turnover by Building</h3>
          <div className={`rounded-lg border overflow-hidden ${tableBorder}`}>
            <table className="w-full text-xs">
              <thead className={tableHead}>
                <tr>
                  <th className={thCls}>Building</th>
                  <th className={`text-right px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Events</th>
                  <th className={`text-right px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rate</th>
                  <th className={thCls}></th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {metrics.per_building_turnover.map(b => {
                  const maxRate = Math.max(1, ...metrics.per_building_turnover.map(x => x.turnover_rate));
                  return (
                    <tr key={b.building_id}>
                      <td className="px-3 py-1.5 truncate max-w-[140px]">{b.building_name}</td>
                      <td className="px-3 py-1.5 text-right">{b.turnover_count}</td>
                      <td className={`px-3 py-1.5 text-right font-medium ${b.turnover_rate > 25 ? 'text-red-500' : b.turnover_rate > 15 ? 'text-orange-500' : 'text-green-500'}`}>{b.turnover_rate}%</td>
                      <td className="px-3 py-1.5 w-20">
                        <div className={`h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <div className={`h-2 rounded-full transition-all ${b.turnover_rate > 25 ? 'bg-red-500' : b.turnover_rate > 15 ? 'bg-orange-500' : 'bg-green-500'}`}
                            style={{ width: `${(b.turnover_rate / maxRate * 100).toFixed(0)}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Next 30 Days Countdown Strip + Upcoming Events — always show */}
      {(
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar size={12} className="text-blue-500" />
              Upcoming Events
            </h3>
          </div>

          {/* Countdown strip */}
          <div className={`flex gap-2 px-4 py-2.5 border-b ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`}>
            {[
              { key: 'all', label: 'All', count: metrics.upcoming_events.length, cls: 'bg-gray-500/10 text-gray-500' },
              { key: 'move_in', label: 'Move-Ins', count: eventCounts.move_in, cls: 'bg-blue-500/10 text-blue-500' },
              { key: 'vacancy', label: 'Vacancies', count: eventCounts.vacancy, cls: 'bg-orange-500/10 text-orange-500' },
              { key: 'lease_expiry', label: 'Lease Exp', count: eventCounts.lease_expiry, cls: 'bg-orange-500/10 text-orange-500' },
              { key: 'recert', label: 'Recerts', count: eventCounts.recert, cls: 'bg-red-500/10 text-red-500' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setEventFilter(f.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  eventFilter === f.key
                    ? `${f.cls} ring-2 ring-current ring-offset-1 ${isDark ? 'ring-offset-gray-800' : 'ring-offset-white'}`
                    : `${f.cls} opacity-60 hover:opacity-100`
                }`}
              >
                {f.label}
                <span className="font-bold">{f.count}</span>
              </button>
            ))}
          </div>

          {/* Events table */}
          <div className="overflow-y-auto" style={{ maxHeight: 240 }}>
            {filteredEvents.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Calendar size={24} className="mx-auto mb-2 opacity-30" />
                <p className="text-xs">{(metrics.upcoming_events?.length || 0) === 0 ? 'No upcoming events. Lease expirations, recerts, and status transitions will appear here.' : 'No events match this filter.'}</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className={`sticky top-0 ${tableHead}`}>
                  <tr>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Unit</th>
                    <th className={thCls}>Building</th>
                    <th className={thCls}>Band</th>
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Detail</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filteredEvents.map((evt, i) => {
                    const typeStyles = {
                      move_in: { label: 'Move-In', cls: 'bg-blue-500/10 text-blue-500' },
                      vacancy: { label: 'Vacancy', cls: 'bg-orange-500/10 text-orange-500' },
                      lease_expiry: { label: 'Lease Exp', cls: 'bg-orange-500/10 text-orange-500' },
                      recert: { label: 'Recert', cls: 'bg-red-500/10 text-red-500' },
                    };
                    const style = typeStyles[evt.type] || typeStyles.recert;
                    return (
                      <tr key={i}>
                        <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${style.cls}`}>{style.label}</span></td>
                        <td className="px-3 py-1.5 font-medium">{evt.unit}</td>
                        <td className="px-3 py-1.5">{evt.building || '—'}</td>
                        <td className="px-3 py-1.5">{evt.designation ? `${evt.designation}%` : '—'}</td>
                        <td className="px-3 py-1.5">{evt.date || '—'}</td>
                        <td className={`px-3 py-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{evt.detail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Recent Status Changes (Audit Trail) */}
      {metrics.recent_changes?.length > 0 && (
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Clock size={12} /> Recent Status Changes
            </h3>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 220 }}>
            <table className="w-full text-xs">
              <thead className={`sticky top-0 ${tableHead}`}>
                <tr>
                  <th className={thCls}>Unit</th>
                  <th className={thCls}>Change</th>
                  <th className={thCls}>Band</th>
                  <th className={thCls}>When</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {metrics.recent_changes.map((c, i) => {
                  const statusColor = (s) =>
                    s === 'occupied' ? 'text-green-500' :
                    s === 'leased' ? 'text-blue-500' :
                    s === 'notice' ? 'text-orange-500' :
                    s === 'vacant' ? 'text-red-500' : '';
                  return (
                    <tr key={i}>
                      <td className="px-3 py-1.5 font-medium">{c.unit}</td>
                      <td className="px-3 py-1.5">
                        <span className={statusColor(c.from)}>{c.from || '—'}</span>
                        <ArrowRight size={10} className="inline mx-1 text-gray-400" />
                        <span className={statusColor(c.to)}>{c.to}</span>
                      </td>
                      <td className="px-3 py-1.5">{c.designation ? `${c.designation}%` : '—'}</td>
                      <td className={`px-3 py-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {c.changed_at ? new Date(c.changed_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {metrics.total_status_changes === 0 && (metrics.upcoming_events?.length || 0) === 0 && (
        <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <Activity size={32} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No status change history yet. Analytics will populate as unit statuses change over time.
          </p>
        </div>
      )}
    </div>
  );
}
